import { supabase } from "../../Services/supabaseClient";
import { ensureCrmPipeline } from "./crmBootstrapService";

export const getOpportunities = async (companyId) => {
  const { data, error } = await supabase
    .from("crm_opportunities")
    .select(
      `
      *,
      crm_accounts(id, name),
      crm_contacts(id, full_name),
      crm_pipeline_stages(id, name, position, default_probability, stage_type)
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};
export const getPipelineStages = async (companyId) => {
  const pipeline = await ensureCrmPipeline(companyId);

  if (!pipeline?.id) {
    return [];
  }

  const { data, error } = await supabase
    .from("crm_pipeline_stages")
    .select("*")
    .eq("pipeline_id", pipeline.id)
    .order("position", { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};

export const createOpportunity = async (payload) => {
  const { data, error } = await supabase
    .from("crm_opportunities")
    .insert([payload])
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const updateOpportunity = async (companyId, opportunityId, payload) => {
  const { data, error } = await supabase
    .from("crm_opportunities")
    .update(payload)
    .eq("company_id", companyId)
    .eq("id", opportunityId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

export const getOpportunityById = async (companyId, opportunityId) => {
  const { data: opportunity, error: opportunityError } = await supabase
    .from("crm_opportunities")
    .select(
      `
      *,
      crm_accounts(id, name, email, phone),
      crm_contacts(id, full_name, email, phone),
      crm_pipeline_stages(id, name, position, stage_type)
    `
    )
    .eq("company_id", companyId)
    .eq("id", opportunityId)
    .single();

  if (opportunityError) {
    throw opportunityError;
  }

  const [quotesRes, activitiesRes, stageHistoryRes] = await Promise.all([
    supabase
      .from("crm_quotes")
      .select("*")
      .eq("company_id", companyId)
      .eq("opportunity_id", opportunityId)
      .order("created_at", { ascending: false }),
    supabase
      .from("crm_activities")
      .select("*")
      .eq("company_id", companyId)
      .eq("related_type", "opportunity")
      .eq("related_id", opportunityId)
      .order("created_at", { ascending: false }),
    supabase
      .from("crm_stage_history")
      .select(
        `
        *,
        from_stage:from_stage_id(id, name),
        to_stage:to_stage_id(id, name)
      `
      )
      .eq("company_id", companyId)
      .eq("opportunity_id", opportunityId)
      .order("changed_at", { ascending: false }),
  ]);

  if (quotesRes.error) {
    throw quotesRes.error;
  }

  if (activitiesRes.error) {
    throw activitiesRes.error;
  }

  if (stageHistoryRes.error) {
    throw stageHistoryRes.error;
  }

  return {
    opportunity,
    quotes: quotesRes.data || [],
    activities: activitiesRes.data || [],
    stageHistory: stageHistoryRes.data || [],
  };
};

export const moveOpportunityStage = async ({
  opportunityId,
  companyId,
  toStageId,
  changedBy,
  note = "",
}) => {
  const { data: currentOpp, error: currentError } = await supabase
    .from("crm_opportunities")
    .select("id, stage_id, status")
    .eq("id", opportunityId)
    .eq("company_id", companyId)
    .single();

  if (currentError) {
    throw currentError;
  }

  const { data: targetStage, error: targetStageError } = await supabase
    .from("crm_pipeline_stages")
    .select("id, default_probability, stage_type")
    .eq("id", toStageId)
    .single();

  if (targetStageError) {
    throw targetStageError;
  }

  const nextStatus =
    targetStage.stage_type === "won"
      ? "won"
      : targetStage.stage_type === "lost"
      ? "lost"
      : "open";

  const { data: updatedOpp, error: updateError } = await supabase
    .from("crm_opportunities")
    .update({
      stage_id: toStageId,
      probability: Number(targetStage.default_probability || 0),
      status: nextStatus,
      actual_close_date:
        nextStatus === "won" || nextStatus === "lost"
          ? new Date().toISOString().slice(0, 10)
          : null,
    })
    .eq("id", opportunityId)
    .eq("company_id", companyId)
    .select()
    .single();

  if (updateError) {
    throw updateError;
  }

  const { error: historyError } = await supabase
    .from("crm_stage_history")
    .insert([
      {
        company_id: companyId,
        opportunity_id: opportunityId,
        from_stage_id: currentOpp.stage_id,
        to_stage_id: toStageId,
        changed_by: changedBy,
        note,
      },
    ]);

  if (historyError) {
    throw historyError;
  }

  return updatedOpp;
};
